import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
  LineChart, Line,
  Area, AreaChart
} from 'recharts';
import { 
  TrendingUp, TrendingDown, Euro, FileText, CreditCard, 
  Activity, Users, Calendar, DollarSign, Download, Loader2 
} from "lucide-react";
import { useFinancialCharts } from "@/hooks/useFinancialCharts";
import { useExportData } from "@/hooks/useExportData";

const COLORS = {
  primary: '#3b82f6',
  success: '#22c55e',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#06b6d4',
  purple: '#8b5cf6',
  orange: '#f97316',
  pink: '#ec4899'
};

const PIE_COLORS = [COLORS.primary, COLORS.success, COLORS.warning, COLORS.danger, COLORS.info];

export const FinancialCharts = () => {
  const {
    monthlyRevenue,
    invoiceStatus,
    paymentMethods,
    activityRevenue,
    summary,
    loading,
    error,
    refetch
  } = useFinancialCharts();
  
  const { exportFinancialSummary, isExporting } = useExportData();

  const handleExportSummary = () => {
    const summaryData = {
      totalBilled: summary.totalRevenue,
      totalPaid: summary.paidAmount,
      totalPending: summary.pendingAmount,
      totalInvoices: summary.totalInvoices,
      totalDebtors: summary.overdueAmount > 0 ? 1 : 0 // Simplificado
    };
    
    exportFinancialSummary(summaryData, `resumen_financiero_${new Date().toISOString().split('T')[0]}`);
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {[1, 2, 3, 4].map(i => (
          <Card key={i} className="shadow-card">
            <CardContent className="p-6">
              <div className="animate-pulse">
                <div className="h-4 bg-muted rounded w-1/3 mb-4"></div>
                <div className="h-32 bg-muted rounded"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card className="shadow-card">
        <CardContent className="p-6 text-center">
          <div className="text-destructive">Error al cargar datos financieros: {error}</div>
          <button onClick={refetch} className="mt-2 text-primary hover:underline">
            Reintentar
          </button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header con botón de exportar */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Dashboard Financiero</h2>
        <Button 
          variant="outline" 
          onClick={handleExportSummary}
          disabled={isExporting || loading}
        >
          {isExporting ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Download className="w-4 h-4 mr-2" />
          )}
          {isExporting ? 'Exportando...' : 'Exportar Resumen'}
        </Button>
      </div>

      {/* Métricas de resumen */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-gradient-card shadow-card hover:shadow-lg transition-smooth">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ingresos Totales</CardTitle>
            <Euro className="h-4 w-4 text-success" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success">
              {summary.totalRevenue.toFixed(2)}€
            </div>
            <div className="flex items-center gap-1 text-xs">
              {summary.monthlyGrowth >= 0 ? (
                <>
                  <TrendingUp className="h-3 w-3 text-success" />
                  <span className="text-success">+{summary.monthlyGrowth.toFixed(1)}%</span>
                </>
              ) : (
                <>
                  <TrendingDown className="h-3 w-3 text-destructive" />
                  <span className="text-destructive">{summary.monthlyGrowth.toFixed(1)}%</span>
                </>
              )}
              <span className="text-muted-foreground">vs mes anterior</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-card shadow-card hover:shadow-lg transition-smooth">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tasa de Pago</CardTitle>
            <CreditCard className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">
              {summary.paymentRate.toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground">
              {summary.paidAmount.toFixed(2)}€ de {summary.totalRevenue.toFixed(2)}€
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-card shadow-card hover:shadow-lg transition-smooth">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Factura Promedio</CardTitle>
            <FileText className="h-4 w-4 text-info" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-info">
              {summary.averageInvoiceValue.toFixed(2)}€
            </div>
            <p className="text-xs text-muted-foreground">
              {summary.totalInvoices} facturas generadas
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-card shadow-card hover:shadow-lg transition-smooth">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pendiente</CardTitle>
            <Calendar className="h-4 w-4 text-warning" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-warning">
              {summary.pendingAmount.toFixed(2)}€
            </div>
            <p className="text-xs text-muted-foreground">
              {summary.overdueAmount.toFixed(2)}€ vencido
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gráfico de ingresos mensuales */}
        <Card className="shadow-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Evolución Mensual
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={monthlyRevenue}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip 
                  formatter={(value, name) => [
                    `${parseFloat(value as string).toFixed(2)}€`,
                    name === 'revenue' ? 'Ingresos' : 'Pagos'
                  ]}
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stackId="1"
                  stroke={COLORS.primary}
                  fill={COLORS.primary}
                  fillOpacity={0.6}
                />
                <Area
                  type="monotone"
                  dataKey="payments"
                  stackId="2"
                  stroke={COLORS.success}
                  fill={COLORS.success}
                  fillOpacity={0.6}
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Gráfico de estados de facturas */}
        <Card className="shadow-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Estados de Facturas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={invoiceStatus}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="amount"
                  label={({ status, percentage }) => `${status}: ${percentage.toFixed(1)}%`}
                >
                  {invoiceStatus.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `${parseFloat(value as string).toFixed(2)}€`} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Gráfico de métodos de pago */}
        <Card className="shadow-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-primary" />
              Métodos de Pago
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={paymentMethods} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="method" type="category" width={100} />
                <Tooltip formatter={(value) => `${parseFloat(value as string).toFixed(2)}€`} />
                <Bar dataKey="amount" fill={COLORS.primary} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Gráfico de ingresos por actividad */}
        <Card className="shadow-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-primary" />
              Ingresos por Actividad
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={activityRevenue}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="activity" />
                <YAxis />
                <Tooltip formatter={(value) => `${parseFloat(value as string).toFixed(2)}€`} />
                <Bar dataKey="revenue" fill={COLORS.success} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Tabla de detalles por actividad */}
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Detalle por Actividad
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3 font-medium">Actividad</th>
                  <th className="text-right py-2 px-3 font-medium">Ingresos</th>
                  <th className="text-center py-2 px-3 font-medium">Facturas</th>
                  <th className="text-right py-2 px-3 font-medium">€/Sesión</th>
                </tr>
              </thead>
              <tbody>
                {activityRevenue.map((activity, index) => (
                  <tr key={index} className="border-b hover:bg-muted/50">
                    <td className="py-2 px-3 font-medium">{activity.activity}</td>
                    <td className="py-2 px-3 text-right font-medium text-success">
                      {activity.revenue.toFixed(2)}€
                    </td>
                    <td className="py-2 px-3 text-center">
                      <Badge variant="outline">{activity.invoices}</Badge>
                    </td>
                    <td className="py-2 px-3 text-right text-muted-foreground">
                      {activity.averagePerSession.toFixed(2)}€
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FinancialCharts;